<template>
  <div>
    <v-dialog v-model="showValidationDialog">
          <v-card class="search-modal">
          <h2>Validation Results</h2>
          <v-form class="search-form">
              <v-alert v-if="!!validationResult && !!validationResult.errors && validationResult.errors.length > 0" type="error">  
                <pre>{{ validationResult.errors.join("\r\n") }}</pre>
              </v-alert>  
              <v-alert v-if="!!validationResult && !!validationResult.warnings && validationResult.warnings.length > 0" type="warning" outlined> 
                <pre>{{ validationResult.warnings.join("\r\n") }}</pre>
              </v-alert> 
              <v-btn
                      large
                      color="primary"
                      width="125px"
                      :disabled="!validationResult.canBeSaved"
                      v-on:click="saveChanges"
                  >                  
                  <v-icon v-if="!validationResult.canBeSaved">mdi-alert</v-icon>
                  Save</v-btn>     
          </v-form>
          </v-card>
    </v-dialog>
    <div class="profile-content">
      <v-row class="mt-3">             
        <v-textarea
          :disabled="!isEditMode"
          :visible="!isEditMode"
          outlined
          auto-grow
          clearable
          rows="1"
          v-model="litterInfo.adminNotes"
          label="Description of changes (required)"
        />
        <v-btn class="ml-1 mr-1" 
          :disabled="isEditMode && (!litterInfo.adminNotes || /^\s*$/.test(litterInfo.adminNotes))"
          color="primary" 
          @click="toggleEditMode">
          {{ isEditMode ? "Save" : "Edit" }}
        </v-btn>
    </v-row>
      <v-row  class="ma-0 pa-0">
        <v-col>          
              <DatepickerFormatted
                  :disabled="!isEditMode"
                  :date="litterInfo.registrationDate"
                  label="Registration Date"
                  v-on:updateDates="updateDates($event, 'registrationDate')"
                />
        </v-col>
        <v-col>          
              <v-checkbox
                :disabled="!isEditMode"
                v-model="litterInfo.isMultiSire"
                label="Is multi-sire"
                />
        </v-col>
        <v-col>          
              <DatepickerFormatted
                  :disabled="!isEditMode"
                  :date="litterInfo.whelpingDate"
                  label="Whelping Date"
                  v-on:updateDates="updateDates($event, 'whelpingDate')"
                />
        </v-col>
        <v-col>          
          <litter-status-dropdown
            required
            :disabled="!isEditMode"
            :status="litterInfo.status"
            v-on:update="litterInfo.status = $event"
          />
        </v-col>
      </v-row>
      <v-row class="ma-0 pa-0">
        <v-col>
          <AdminDogSearchDialog 
            label="Dam"
            v-bind:dogId.sync="litterInfo.damId"
            :initialDogId="persistedLitterInfo.damId" 
            :showOwners="true"
            :returnOwners="true"
            :isEditMode="isEditMode"
            @update-owners="updateOwners" />
        </v-col>
        <v-divider vertical light class="d-none d-sm-flex"></v-divider>
        <v-col>          
          <AdminDogSearchDialog 
            label="Sire"
            v-bind:dogId.sync="litterInfo.sireId"
            :initialDogId="persistedLitterInfo.sireId" 
            :showOwners="true"
            :isEditMode="isEditMode" />                 
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <h2 class="blue-grey--text text--darken-1">Overlapping leases:</h2>       
          <div v-if="overlappingDamLeases.length > 0">
            <div v-for="lease in overlappingDamLeases"  v-bind:key="lease.leaseId">
              <v-row>
                    <v-col cols="12">
                      <h4>
                       ID #<a :href="`/admin/leases/${lease.leaseId}`" target="_blank">{{ lease.leaseId }}</a>: {{ $dateUtils.formatDate(lease.startDate) }} - {{ $dateUtils.formatDate(lease.endDate) }}
                      </h4>
                    </v-col>
              </v-row>
              <AdminEditableCoownersList :owners="lease.lessees" :ownershipType="leaseOwnershipType" :isEditMode="false" :isReadOnlyMode="true" /> 
            </div>
          </div>
          <div v-else>
            No leases found overlapping with the whelping date
          </div>
        </v-col>
        <v-col>
            <AdminEditableCoownersList
                :isEditMode="isEditMode"
                :owners="litterInfo.owners"
                :ownershipType="litterOwnershipType"
                v-on:updateSelectedPeople="updateSelectedPeople($event)"
                v-on:personAdded="handleLitterCoownerAdded($event)"                
              />
        </v-col>
        <v-col>
            <AdminKennelSearchDialogOld
              :kennelSearchDialog="showSearchKennelDialog"
              v-on:kennelAdded="handleKennelAdded"
            />
            <v-data-table
              :items="litterInfo.kennels"
              :headers="litterKennelsHeaders"
              hide-default-footer
              hide-default-header
              disable-pagination
              class="elevation-1 full-width"
              no-data-text="No associated kennels"
            >    
              <template v-slot:top>
                <v-toolbar flat>
                  <h2 class="blue-grey--text text--darken-1">
                    Associated Kennels:
                  </h2>
                  <v-spacer></v-spacer>
                  <v-btn color="primary" v-on:click="toggleKennelSearchDialog">
                    + Add kennel
                  </v-btn>
                </v-toolbar>      
              </template>
              <template v-slot:item.id="{ item }">
                <router-link :to="`/admin/kennels/${item.id}`" target="_blank">
                  {{ item.id }} 
                </router-link>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-icon small @click="deleteKennel(item)"> mdi-delete </v-icon>
              </template>
            </v-data-table>
        </v-col>
      </v-row>   
      <v-row>
          <v-textarea
            disabled
            @input="updateText('notes', $event)"
            :value="litterInfo.notes"
            outlined
            rows="1"
            auto-grow
            label="Notes"
          ></v-textarea>
      </v-row>
      <v-snackbar v-model="showInfoMessage" title color="green">
        {{ infoMessage }}
        <template v-slot:action="{ attrs }">
          <v-btn color="primary" text v-bind="attrs" @click="showInfoMessage = false">
            Close
          </v-btn>
        </template>
      </v-snackbar>
      <v-snackbar v-model="showErrorMessage" title color="red">
        {{ errorMessage }}
        <template v-slot:action="{ attrs }">
          <v-btn color="primary" text v-bind="attrs" @click="showErrorMessage = false">
            Close
          </v-btn>
        </template>
      </v-snackbar>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import AdminEditableCoownersList, { OwnershipType } from "@/components/Admin/AdminEditableCoownersList.vue";
import AdminDogSearchDialog from "@/components/Admin/AdminDogSearchDialog.vue";
import AdminKennelSearchDialogOld from "@/components/Admin/AdminKennelSearchDialogOld.vue";
import DatepickerFormatted from "@/components/common/inputs/DatepickerFormatted.vue";
import LitterStatusDropdown from "@/components/common/inputs/LitterStatusDropdown.vue"

import litterService from "@/services/admin/litterService";

import { Messages } from "@/constants";
import { UserInfo } from "@/types/User";

export default Vue.extend({
  name: "LitterEntry",
  components: {
    AdminEditableCoownersList,
    AdminDogSearchDialog,
    DatepickerFormatted,
    AdminKennelSearchDialogOld,
    LitterStatusDropdown,
  },
  data: function() {
    return {
      litterInfo: {
        litterId: null, 
        owners: [] as any,
        kennels: [] as any,
        whelpingDate: null,
        isMultiSire: false,
        registrationDate: (new Date()).toISOString(),
        damId: null,
        sireId: null,
        adminNotes: '',
        notes: '',
        status: null,
      } as any,      
      persistedLitterInfo: {
        litterId: null, 
        owners: [] as any,
        kennels: [] as any,
        whelpingDate: null,
        isMultiSire: false,
        registrationDate: (new Date()).toISOString(),
        damId: null,
        sireId: null,
        adminNotes: '',
        notes: '',
        status: null,
      } as any,
      isEditMode: false,
      showFormDialog: false,
      showValidationDialog: false, 
      showSearchKennelDialog: false,
      validationResult: {
        warnings: [],
        errors: []
      },     
      overlappingDamLeases: [],
      litterKennelsHeaders:  [
        { text: "Id", value: "id" },
        { text: "Name", value: "name" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      errorMessage: '',
      infoMessage: '',
      showErrorMessage: false,
      showInfoMessage: false
    };
  },
  methods: {
    toggleKennelSearchDialog: function () {
      this.showSearchKennelDialog = !this.showSearchKennelDialog;
    },
    deleteKennel: function(kennel: any){
      const index = this.litterInfo.kennels.map((k: any) => k.kennelId).indexOf(kennel.kennelId);
      (this as any).litterInfo.kennels.splice(index, 1);
    },
    toggleEditMode: function () {
      if (this.isEditMode) {
        this.validateAndConfirmSavingChanges();
      } else {
        this.isEditMode = true;
      }
    },
    validateAndConfirmSavingChanges: function () {
      const litterData = {
          litterId: this.litterInfo.litterId,
          damId: this.litterInfo.damId,
          sireId: this.litterInfo.sireId,
          whelpingDate: this.litterInfo.whelpingDate,
          owners: this.litterInfo.owners,
          kennelIds: this.litterInfo.kennels.map((k: any) => k.id),
          adminNotes: this.litterInfo.adminNotes,
          isMultiSire: this.litterInfo.isMultiSire,
          registrationDate: this.litterInfo.registrationDate
        };
      litterService
        .validateLitterInfoEdit(litterData)
        .then((res: any) => {
          this.validationResult = res;
          if(res.hasValidationIssues)
          {
            this.showValidationDialog = true;
          } else {
            this.saveChanges();
          }
        })  
    },
    saveChanges: function () {
      this.showValidationDialog = false;
      this.$store.dispatch("changeLoaderStatus",true);
      const litterData = {
          litterId: this.litterInfo.litterId,
          damId: this.litterInfo.damId,
          sireId: this.litterInfo.sireId,
          whelpingDate: this.litterInfo.whelpingDate,
          owners: this.litterInfo.owners,
          kennelIds: this.litterInfo.kennels.map((k: any) => k.id),
          adminNotes: this.litterInfo.adminNotes,
          isMultiSire: this.litterInfo.isMultiSire,
          registrationDate: this.litterInfo.registrationDate,
          status: this.litterInfo.status,
        };
      litterService
                .editLitterById(this.litterInfo.litterId, litterData)
                .then((res: any) => {
                  if(res.success){
                    if(res.id && res.id != this.litterId){
                      this.$router.push({ path: `/admin/litters/${encodeURIComponent(res.id.trim())}` }).catch(this.$handleNavigationError);
                    }
                    this.isEditMode = false;
                    this.reloadData();
                    this.infoMessage = Messages.SaveSuccessful;
                    this.showInfoMessage = true;
                  } else {              
                    this.errorMessage = Messages.SaveError + " " + res.messages.join(', ')
                    this.showErrorMessage = true;
                  }
                })
                .catch((e: any) => {
                  this.errorMessage = Messages.SaveError;
                  this.showErrorMessage = true;
                })
                .finally(() => this.$store.dispatch("changeLoaderStatus",false));
    },
    handleKennelAdded: function(val :any){
      let kennels = this.litterInfo.kennels;
      const kennelsIds = this.litterInfo.kennels.map((c: any) => c.id);
      const index = kennelsIds.indexOf(val.kennel.kennelId);
      if(index < 0){
        kennels.push({id: val.kennel.kennelId, name: val.kennel.name });
      }
      this.litterInfo.kennels = kennels;
    },
    getKennelsForPeople: function(ids: any[]) {      
      litterService.getKennelsCoownedBy(ids)
        .then((res) => {
          let kennels = this.litterInfo.kennels;
          const kennelsIds = this.litterInfo.kennels.map((c: any) => c.id);

          res.data.forEach((k: any) => {
            const index = kennelsIds.indexOf(k.id.trim());
            if(index < 0){
              kennels.push(k);
            }
          });
          this.litterInfo.kennels = kennels;
        });
    },
    handleLitterCoownerAdded: function (val: any) {
      this.getKennelsForPeople([val.personId]);
    },
    updateSelectedPeople: function (val: any) {
      //workaround for setting up the primary owner
      //and issues with reactivity in Vue https://v2.vuejs.org/v2/guide/reactivity.html?redirect=true#For-Arrays
      if(val.newPeople && (val.newPeople.length === 1 || (val.newPeople.length > 0 && val.newPeople.findIndex((own: any) => own.isPrimary) == -1)))
      {
        val.newPeople[0].isPrimary = true;
      }
      if(!!this.litterInfo.owners && val.newPeople && val.newPeople.length > 0)
      {
        this.litterInfo.owners.splice(val.newPeople.length);
      }
      this.$set(this.litterInfo, 'owners', val.newPeople);
    },
    reloadData: function() {
      this.showValidationDialog = false;
      this.validationResult = {
        warnings: [],
        errors: []
      };
      this.errorMessage = '';
      if (this.litterId) {
        litterService
          .getLitterById(this.litterId)
          .then((res) => {
            this.litterInfo = res;
            this.persistedLitterInfo = res;
            this.getOverlappingDamLeases(false);
          });
      } else { //make the screen editable if it's create new
        this.isEditMode = true;
      }
    }, 
    updateDates: function (val: any, key: string) {
      this.litterInfo[key] = val.date;
      this.litterInfo = Object.assign({}, this.litterInfo);
    },
    getOverlappingDamLeases(updateOwners: boolean) {
      if(this.litterInfo.damId && this.litterInfo.whelpingDate){
        this.$store.dispatch("changeLoaderStatus",true);
        litterService
                .getOverlappingLeasesForDam(this.litterInfo.damId, this.litterInfo.whelpingDate)
                .then((res: any) => {
                  this.overlappingDamLeases = res.data;
                  if (updateOwners) this.updateOwnersFromODLs();
                })
                .catch(this.$handleError)
                .finally(() => this.$store.dispatch("changeLoaderStatus",false));

      } else {
        this.overlappingDamLeases = [];
      }
    },
    updateOwners: function(owners: any[], leaseOwners?: boolean) {
      if (this.isEditMode && ((this.overlappingDamLeases.length && leaseOwners) || !this.overlappingDamLeases.length)) {
        this.litterInfo.owners = owners;
        if (owners.length) this.getKennelsForPeople(owners.map((o) => o.personId));
      }
    },
    updateOwnersFromODLs: function() {
      let owners: any[] = [];

      for (let lease of this.overlappingDamLeases) {
        owners = owners.concat([...(lease as any).lessees]);
      }

      this.updateOwners(owners, true);
    },
  },
  created: function() {
    this.reloadData();
  },
  watch: {
    litterId: function(newVal: String) {
      this.reloadData();
    },
    "litterInfo.whelpingDate": function(newVal: String) {
      this.getOverlappingDamLeases(true);
    },
    "litterInfo.damId": function(newVal: String) {
      this.getOverlappingDamLeases(true);
    },
  },
  props: {
    litterId: { type: Number },
  },
  computed: {
    userInfo(): UserInfo {
      return this.$store.state.userInfo;
    },
    dogOwnershipType: function() {
      return OwnershipType.Dog;
    },
    litterOwnershipType: function() {
      return OwnershipType.Litter;
    },
    leaseOwnershipType: function() {
      return OwnershipType.Lease;
    },
  },
});
</script>
